import { animated, useSpring, useSprings } from '@react-spring/web';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { MediaAssets } from '../services/ofertas-service/models/ofertas';

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	position: relative;
`;

const Container = styled.div`
	width: 100vw;
	overflow: hidden;
	height: 100%;
	position: relative;
	z-index: 0;
`;

const CarImagesRow = styled(animated.div)`
	display: flex;
	flex-direction: row;
	align-items: center;
	position: relative;
	height: 100%;
	white-space: nowrap;
`;

const CarImage = styled(animated.img)`
	height: 100%;
	flex-shrink: 0;
	object-fit: contain;
	width: clamp(150px, 42vw, 925px);

	@media screen and (min-width: 962px) and (max-width: 1279px) {
		height: 265px;
	}
`;

const ControlsContainer = styled.div`
	position: absolute;
	top: 55px;
	left: 75%;
	transform: translateX(-50%);
	z-index: 1;
	pointer-events: auto;
`;

const ControlButton = styled.button`
	color: #001e50;
	border: 1px solid #001e50;
	border-radius: 20px;
	padding: 6px 16px;
	font-size: 14px;
	font-weight: bold;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	transition:
		border-color 0.2s ease-in-out,
		background-color 0.2s ease-in-out;
	white-space: nowrap;

	&:hover {
		background-color: #0040c4;
		color: #ffffff;
		border-color: #0033a0;
	}
`;

export const Carrousel = ({
	mediaAssets,
}: {
	mediaAssets: MediaAssets;
}): React.ReactElement => {
	const turntable = [
		'D6Turntable1',
		'D6Turntable3',
		'D6Turntable5',
		'D6Turntable6',
		'D6Turntable7',
		'D6Turntable9',
		'D6Turntable12',
	];

	const carImages = turntable
		.map((t) => mediaAssets[t])
		.filter(
			(image): image is string => typeof image === 'string' && image.length > 0,
		);

	const loopImages = [...carImages, ...carImages, ...carImages];
	const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
	const [imageWidth, setImageWidth] = useState(0);
	const imageRef = useRef<HTMLImageElement | null>(null);
	const [isPaused, setIsPaused] = useState(false);

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Safe observer
	useEffect(() => {
		if (!imageRef.current || typeof ResizeObserver === 'undefined') return;

		const observer = new ResizeObserver((entries) => {
			const width = entries[0].contentRect.width;
			setImageWidth(width);
		});

		observer.observe(imageRef.current);
		return () => observer.disconnect();
	}, []);

	const [{ x }, api] = useSpring(() => ({
		from: { x: 0 },
		to: { x: 0 },
		config: { duration: 120000 },
	}));

	useEffect(() => {
		if (!imageWidth || carImages.length === 0) return;

		const startIndex = carImages.length;
		const initialOffset =
			-startIndex * imageWidth + (windowWidth / 2 - imageWidth / 2 - 120);
		const targetOffset = initialOffset - imageWidth * carImages.length;

		api.start({
			from: { x: initialOffset },
			to: { x: targetOffset },
			loop: true,
		});

		return () => api.stop();
	}, [api, imageWidth, windowWidth, carImages.length]);

	useEffect(() => {
		if (isPaused) {
			api.pause();
		} else {
			api.resume();
		}
	}, [isPaused, api]);

	const menuWidth = 250;
	const center = windowWidth / 2;
	const fadeStart = center - menuWidth - imageWidth / 2;
	const fadeEnd = fadeStart + 150;

	const springs = useSprings(
		loopImages.length,
		loopImages.map((_, i) => ({
			opacity: x.to((val) => {
				const imageCenterX = i * imageWidth + val + imageWidth / 2;
				const distance = center - imageCenterX;
				if (distance < fadeStart) return 1;
				if (distance > fadeEnd) return 0.25;
				const fadeRange = fadeEnd - fadeStart;
				const relative = (distance - fadeStart) / fadeRange;
				return 1 - relative * 0.75;
			}),
		})),
	);

	return (
		<>
			<ControlsContainer>
				{/* <ControlButton
					onClick={() => setIsPaused(!isPaused)}
					aria-pressed={isPaused}
					aria-label={isPaused ? 'Reanudar animación' : 'Pausar animación'}
					title={isPaused ? 'Reanudar' : 'Pausar'}
				>
					{isPaused ? (
						<>
							<span>▶</span>
							<span>Reanudar</span>
						</>
					) : (
						<>
							<span>❚❚</span>
							<span>Pausar</span>
						</>
					)}
				</ControlButton> */}
			</ControlsContainer>
			<Wrapper>
				<Container>
					<CarImagesRow
						style={{
							width: `${loopImages.length * imageWidth}px`,
							transform: x.to((val) => `translateX(${val}px)`),
						}}
					>
						{loopImages.map((image, index) => (
							<CarImage
								key={index}
								ref={index === 0 ? imageRef : null}
								src={image}
								alt={`Imagen del coche ${index + 1}`}
								style={{ opacity: springs[index].opacity }}
							/>
						))}
					</CarImagesRow>
				</Container>
			</Wrapper>
		</>
	);
};
